import type { Sprachschluessel } from './sprache'

/**
 * Die Anleitungen — der eine Vorrat für Hilfe und Begleiter (7.1 Abschnitte 9
 * und 10, R304, R545).
 *
 * **Die Schritte stehen hier und nicht in der Datenbank.** Eine Anleitung ist
 * **Bedeutung** und keine Ausprägung (R146): Sie ändert sich, wenn sich die
 * Software ändert, und beides gehört in dieselbe Ausgabe. Eine gepflegte
 * Hilfetabelle ginge irgendwann gegen die Oberfläche, die sie beschreibt.
 *
 * **Und sie stehen genau einmal.** Die Hilfe blättert durch sie, der
 * Begleiter hakt sie ab — zwei Leser, eine Quelle. Zwei Beschreibungen
 * desselben Weges liefen mit der Zeit auseinander, und niemand merkte es.
 */

/**
 * Woran das Modell erkennt, dass ein Schritt getan ist (R546).
 *
 * Jeder Zähler ist eine Menge, die das Modell ohnehin führt. Ist er seit dem
 * Anlegen der Liste gestiegen, gilt der Schritt als erledigt — **eingesammelt,
 * nicht erfunden** (R140). Das ist bewusst grob: Der Begleiter weiß nicht,
 * *welche* Gesellschaft gemeint ist, nur *dass* eine dazugekommen ist. Mehr zu
 * behaupten hieße raten (R142).
 */
export type Zaehler =
  | 'einheiten'
  | 'beteiligungen'
  | 'kreiszugehoerigkeiten'
  | 'datenquellen'
  | 'quellenVollstaendig'
  | 'zuordnungen'
  | 'uebernahmen'
  | 'konten'
  | 'kontozuordnungen'

export type Schritt = {
  /** Die Kennung der Aufnahme — derselbe Name wie in `hilfebilder.json`. */
  id: string
  titel: string
  text: string
  /** Wohin der Schritt führt — dieselbe Adresse, die die Seitenleiste benutzt. */
  ziel?: { bereich: string; unterpunkt: string }
  /**
   * Der Zähler, an dem das Modell den Schritt erkennt (R546). Fehlt er, weiß
   * es nur der Anwender — und dann bleibt der Schritt offen, bis er ihn abhakt.
   */
  zaehler?: Zaehler
}

export type Anleitung = {
  id: string
  titel: Sprachschluessel
  schritte: Schritt[]
}

const IMPORTSCHRITTE: Schritt[] = [
  {
    id: '01-quellen',
    titel: 'Die Datenquelle anlegen',
    text:
      'Jede Lieferung kommt von irgendwoher — aus der Buchhaltung einer Tochter, aus einer Datenbank, aus einem Altsystem. Diese Herkunft wird einmal eingerichtet und dann bei jedem Import benutzt. Öffnen Sie Datenimport → Datenquellen: Links stehen die Quellen, jede mit ihrem Zustand und der Zahl ihrer offenen Felder; rechts das Blatt — dasselbe wie im Kontenrahmen. Drücken Sie „Neu anlegen".',
    ziel: { bereich: 'datenimport', unterpunkt: 'quellen' },
    zaehler: 'datenquellen',
  },
  {
    id: '02-quellenblatt',
    titel: 'Code, Bezeichnung und Art eintragen',
    text:
      'Der Code wird zum Schlüssel und lässt sich später nicht mehr ändern — alles andere schon. Die Art muss gleich mit dazu: Ohne sie weiß die Software nicht, wie die Quelle gelesen wird. Die Gesellschaft gehört nicht hierher — sie wird bei jedem Import gewählt, denn eine Quelle gilt für jede Gesellschaft, die so liefert. Die Bewertungsebene dagegen sagt die Quelle: als fester Wert „Rohdaten", oder aus einer Spalte, wenn die Datei sie nennt. Danach die Abschnitte darunter: Unter „Die Datei" laden Sie eine Beispieldatei (sie wird nur gelesen, nicht übernommen), wählen das Tabellenblatt und zählen Kopf- und Fußzeilen. Unter „Die Spaltenzuordnung" ziehen Sie einen Spaltenkopf auf ein Feld oder klicken erst die Spalte, dann das Feld; Konto und Betrag sind Pflicht. Was die Datei nicht bringt, bekommt einen festen Wert aus dem Vorrat — oder Sie haken es als „nicht geliefert" ab, damit es nicht als offen zählt. Der Satz über dem Speichern-Knopf sagt die ganze Spaltenzuordnung in einer Zeile. In „Herkunft" gehört, wo die Datei herkommt — kein Kennwort.',
    ziel: { bereich: 'datenimport', unterpunkt: 'quellen' },
    zaehler: 'quellenVollstaendig',
  },
  {
    id: '02b-werte',
    titel: 'Die Werte anlegen, die die Datei mitbringt',
    text:
      'Eine neue Gesellschaft bringt eigene Kostenstellen, Profit-Center oder Partner mit. Was die Datei in einer gebundenen Spalte nennt, muss im Vorrat stehen — sonst fällt die Zeile mit „ist im Vorrat nicht bekannt", und das ist richtig so: Was unbekannt ist, bekommt einen Namen, bevor es gebucht wird. Legen Sie die Werte unter Stammdaten → Optionale Dimensionen (Dimensionswert) beziehungsweise Partnercodes an. Oder Sie haken das Feld an der Quelle als „nicht geliefert" ab, wenn die Spalte nicht gelesen werden soll.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'analysedimensionen' },
  },
  {
    id: '03-mapping',
    titel: 'Nachsehen, was schon zugeordnet ist',
    text:
      'Die liefernde Gesellschaft bucht auf ihren eigenen Kontonummern. Damit daraus ein Konzernabschluss wird, muss jede lokale Nummer auf ein Konzernkonto zeigen. Unter Datenimport → Zuordnungen steht, was schon gilt — für alle Gesellschaften, und in der Spalte „Position“ die Stelle in Bilanz oder GuV. Suchen Sie nach einer Nummer oder einem Namen, oder wählen Sie die Gesellschaft: Dann stehen nur die Zuordnungen da, die ihre Lieferungen verwenden. Was noch fehlt, ordnen Sie beim Prüfen der Datei zu — im Schritt „Unbekannte Konten annehmen“.',
    ziel: { bereich: 'datenimport', unterpunkt: 'importmapping' },
    zaehler: 'zuordnungen',
  },
  {
    id: '04-lieferung',
    titel: 'Die Datei prüfen',
    text:
      'Unter Datenimport → Importieren wählen Sie die Quelle, prüfen Periode und Gesellschaft und übergeben die Datei. „Prüfen" schreibt noch nichts: Die Datei wird abgelegt und gelesen, und Sie bekommen zu sehen, was daraus würde.',
    ziel: { bereich: 'datenimport', unterpunkt: 'lieferungen' },
  },
  {
    id: '05-vorschau',
    titel: 'Unbekannte Konten annehmen',
    text:
      'Trägt die Datei ein Konto, das noch nicht zugeordnet ist, steht es hier mit einem Vorschlag. Der Vorschlag wird nie von selbst wirksam — auch nicht bei voller Übereinstimmung, denn ein falsch zugeordnetes Konto läuft in jeder Folgeperiode weiter. Ein Klick auf „Zuordnen" schreibt die Zuordnung und prüft die Datei sofort noch einmal.',
    ziel: { bereich: 'datenimport', unterpunkt: 'lieferungen' },
  },
  {
    id: '06-uebernehmen',
    titel: 'Übernehmen',
    text:
      'Erst dieser Knopf schreibt. Übernommen wird genau das, was Sie gesehen haben. Die Lieferung ersetzt den Bestand dieser Gesellschaft für Bewertungsebene und Periode: Der vorige Lieferbeleg wird ganz storniert, dann der neue geschrieben — eine Datei zweimal zu liefern verdoppelt deshalb nichts.',
    ziel: { bereich: 'datenimport', unterpunkt: 'lieferungen' },
    zaehler: 'uebernahmen',
  },
  {
    id: '07-bilanz',
    titel: 'Nachsehen, dass die Zahlen dastehen',
    text:
      'Der Abschluss wird nicht von Hand gerechnet: Sobald Daten vorliegen, holt die Anwendung den Lauf selbst nach. Stellen Sie oben den Zeitraum auf die gelieferte Periode und öffnen Sie Berichte → Konzernbilanz. Steht dort nichts, sagt der Bildschirm, woran es liegt — meist fehlt eine andere Gesellschaft oder ein Wechselkurs.',
    ziel: { bereich: 'berichte', unterpunkt: 'bilanz' },
  },
]

/**
 * Strukturen anlegen und ändern — der zweite Leitfaden (R329).
 *
 * **Warum ein eigener und kein Anhang an den ersten:** Der Import ist ein Weg,
 * den man *jeden Monat* geht; das Anlegen eines Kontos ist einer, den man
 * *selten* geht und dann genau wissen muss. Zwei Anlässe, zwei Anleitungen —
 * und die Schrittleiste jeder von beiden bleibt kurz genug, um sie zu
 * überblicken.
 */
const STRUKTURSCHRITTE: Schritt[] = [
  {
    id: 'k1-kontenrahmen',
    titel: 'Den Kontenrahmen öffnen',
    text:
      'Konten stehen unter Einrichten und pflegen → Stammdaten → Kontenrahmen. Links steht die Liste, in der Mitte das Blatt des gewählten Kontos, rechts seine Fassungen. Über der Liste steht „Neu anlegen" — derselbe Knopf wie bei jeder anderen Stammdatenart, denn angelegt wird überall gleich.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'kontenrahmen' },
  },
  {
    id: 'k2-anlegen',
    titel: 'Nummer, Bezeichnung und Kontoart eintragen',
    text:
      'Drei Angaben genügen. Die Nummer wird zum Schlüssel (aus 6250 wird KTO-6250) und lässt sich später nicht mehr ändern — alles andere schon. Die Kontoart steht hier und nicht erst danach, weil es ein Konto ohne sie nicht gibt: An ihr hängt jeder Rechenweg. Dieselbe Nummer darf es übrigens zweimal geben, solange sich die Bezeichnung unterscheidet; zwei Konten, die sich in nichts unterscheiden, was ein Mensch liest, werden abgewiesen.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'kontenrahmen' },
    zaehler: 'konten',
  },
  {
    id: 'k3-gruppe',
    titel: 'Die Kontengruppe setzen — der Rest kommt von selbst',
    text:
      'Nach dem Anlegen steht das Konto offen vor Ihnen. Wichtigstes Feld ist die Kontengruppe: Aus ihr erbt das Konto Umrechnungsmethode, Bewegungsartpflicht, Zahlungswirksamkeit und Fondszugehörigkeit. Was geerbt ist, steht gedämpft da und nennt seine Herkunft — „geerbt von KG-AUFWAND". Nur was hier wirklich abweicht, tragen Sie ein; „Überschreiben" und „Erben" sind beide ein Handgriff.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'kontenrahmen' },
  },
  {
    id: 'k4-zuordnung',
    titel: 'Das Konto in die Gliederung hängen',
    text:
      'Ein angelegtes Konto steht noch in keiner Bilanz: Erst die Zuordnung sagt, in welcher Position es erscheint. Öffnen Sie Stammdaten → Positionspläne, wählen Sie links die Stammdatenart „Konto in der Gliederung" und drücken Sie „Neu anlegen". Konto und Position suchen Sie aus, „Gültig ab" ist die Periode, ab der die Zuordnung wirkt — ein abgeschlossener Vormonat bleibt davon unberührt.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'positionsplaene' },
    zaehler: 'kontozuordnungen',
  },
  {
    id: 'k5-baum',
    titel: 'Nachsehen, wo es hängt',
    text:
      'Über der Pflege steht die Gliederung als Baum, gezeichnet wie die Bilanz. Ihr Konto hängt jetzt unter seiner Position: Klappen Sie die Position mit dem Pfeil davor auf, dann steht es darunter, eingerückt und mit „·“ davor. Von dort lässt es sich mit der Maus woandershin ziehen — oder anklicken und am Ziel wieder anklicken. Damit ist das Konto fertig: Es hat eine Bedeutung (Kontoart und Gruppe) und eine Stelle im Abschluss.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'positionsplaene' },
  },
  {
    id: 'k6-umbenennen',
    titel: 'Ein Konto umbenennen',
    text:
      'Zurück im Kontenrahmen: Wählen Sie das Konto und schreiben Sie in der Zeile seiner Sprache die neue Bezeichnung. Gespeichert wird beim Verlassen des Feldes oder mit der Eingabetaste — nicht bei jedem Buchstaben, denn jede Änderung ist eine neue Fassung. Was sich dabei NICHT ändert: die Nummer und damit der Schlüssel. Und freigegebene Abschlüsse behalten die Bezeichnung von damals, weil sie auf ihrem Stand lesen; wer wann umbenannt hat, steht im Änderungsnachweis.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'kontenrahmen' },
  },
]

/**
 * Eine Gesellschaft anlegen und einbeziehen — der dritte Leitfaden (7.1
 * Abschnitt 10, aus der Testreise „neue Gesellschaft liefert", 9.3).
 *
 * **Drei Schritte, und keiner davon ist die Lieferung.** Die Testreise vom
 * 2026-09-14 hatte es gezeigt: Eine Gesellschaft ließ sich anlegen und hing an
 * niemandem — ohne Beteiligung keine Kapitalkonsolidierung, ohne
 * Kreiszugehörigkeit keine Spalte in irgendeinem Bericht (7.4 Abschnitt 17).
 * Wer die Lieferung als Erstes versucht, sucht danach die Gesellschaft in der
 * Bilanz und findet sie nicht. Deshalb steht dieser Weg **vor** dem Import,
 * und der Begleiter kettet beide zusammen (R545).
 */
const GESELLSCHAFTSSCHRITTE: Schritt[] = [
  {
    id: 'g1-einheit',
    titel: 'Die Gesellschaft anlegen',
    text:
      'Eine Gesellschaft steht zuerst im Stammdatenkatalog. Öffnen Sie Stammdaten → Gesellschaften und drücken Sie „Neu anlegen": Code, Bezeichnung, Land und Währung. Der Code wird zum Schlüssel der Gesellschaft und lässt sich später nicht mehr ändern — alles andere schon, auch die Bezeichnung in der zweiten Sprache, die Sie danach auf demselben Blatt nachtragen. Damit gibt es die Gesellschaft, aber sie hängt noch an niemandem und erscheint in keinem Bericht.',
    ziel: { bereich: 'stammdaten', unterpunkt: 'einheiten' },
    zaehler: 'einheiten',
  },
  {
    id: 'g2-beteiligung',
    titel: 'Die Beteiligung und die Erstkonsolidierung eintragen',
    text:
      'Wer hält die Gesellschaft, zu welcher Quote, ab wann und wie wird sie einbezogen? Das steht unter Konzernstruktur → Beteiligungsregister: Stammdatenart „Beteiligung", „Neu anlegen", Tochter und Halter auswählen, die Quote als Zahl (1 für 100 %), Einbeziehungsart „voll", gültig ab der ersten Periode. Der Schlüssel entsteht aus beiden Enden — dieselbe Tochter unter zwei Haltern gibt zwei Zeilen. Danach die Erstkonsolidierung als eigene Stammdatenart: je Eigenkapitalkonto ein Betrag, so wie er in der Bilanz der Tochter steht, die Passivseite mit Minus.',
    ziel: { bereich: 'konzernstruktur', unterpunkt: 'beteiligungsregister' },
    zaehler: 'beteiligungen',
  },
  {
    id: 'g3-kreis',
    titel: 'Die Gesellschaft in ihre Kreise aufnehmen',
    text:
      'Erst die Kreiszugehörigkeit bringt die Gesellschaft in einen Bericht: Ohne sie steht sie in keinem Konsolidierungskreis und damit in keiner Konzernbilanz. Unter Konzernstruktur → Konsolidierungskreise öffnen Sie den Kreis und nehmen die Gesellschaft im Abschnitt „Wer dazugehört“ über „Gesellschaft hinzufügen“ auf, gültig ab derselben Periode. Eine Gesellschaft in zwei Kreisen — Teilkonzern und Gesamtkonzern — bekommt zwei Zeilen. Danach steht sie als Spalte in jedem Bericht ihres Kreises, noch ohne Zahlen.',
    ziel: { bereich: 'konzernstruktur', unterpunkt: 'kreise' },
    zaehler: 'kreiszugehoerigkeiten',
  },
]

/**
 * Welche Anleitung unter welchem Unterpunkt steht.
 *
 * **Der Unterpunkt ist die Adresse** (R156): `#/hilfe/struktur` führt
 * dorthin und ist verlinkbar — aus einer Meldung, aus einer Arbeitsliste, aus
 * einer Mail an den Kollegen.
 */
export const ANLEITUNGEN: Record<string, Anleitung> = {
  import: { id: 'import', titel: 'bereich.hilfe.import', schritte: IMPORTSCHRITTE },
  struktur: { id: 'struktur', titel: 'bereich.hilfe.struktur', schritte: STRUKTURSCHRITTE },
  gesellschaft: { id: 'gesellschaft', titel: 'bereich.hilfe.gesellschaft', schritte: GESELLSCHAFTSSCHRITTE },
}

/**
 * Ein Weg — eine Folge von Anleitungen (R545).
 *
 * Der Vorrat ist fest und steht hier neben den Anleitungen, aus demselben
 * Grund wie sie: Ein Weg ist Bedeutung. Wer einen vierten braucht, schreibt
 * ihn hierher — und nicht in eine Tabelle, die niemand gegen die Oberfläche
 * prüft.
 */
export type Weg = {
  id: string
  titel: Sprachschluessel
  anleitungen: string[]
}

export const WEGE: Weg[] = [
  { id: 'neue-gesellschaft', titel: 'begleiter.weg.neuegesellschaft', anleitungen: ['gesellschaft', 'import'] },
  { id: 'import', titel: 'bereich.hilfe.import', anleitungen: ['import'] },
  { id: 'gesellschaft', titel: 'bereich.hilfe.gesellschaft', anleitungen: ['gesellschaft'] },
  { id: 'struktur', titel: 'bereich.hilfe.struktur', anleitungen: ['struktur'] },
]

/** Die Schritte eines Weges in ihrer Reihenfolge, jeder mit seiner Anleitung. */
export function wegschritte(weg: Weg): { anleitung: string; schritt: Schritt }[] {
  return weg.anleitungen.flatMap((id) =>
    (ANLEITUNGEN[id]?.schritte ?? []).map((schritt) => ({ anleitung: id, schritt })),
  )
}
